import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../hooks/useLocale';
import { computeTtfPriceMxn, formatTtfMxn } from '../utils/ttf';
import { trackTtfStep, TTF_STEPS } from '../services/analyticsService';

/**
 * Customer-facing TWD → final MXN price calculator for pieces seen at the
 * festival (e.g. before sending a piece request). Uses the same rule as the
 * catalog (computeTtfPriceMxn) and shows only the final price — never the
 * exchange rate or the percentage.
 */
const TtfPriceCalculator = () => {
  const { t } = useTranslation('ttf');
  const locale = useLocale();
  const [ntd, setNtd] = useState('');
  const price = computeTtfPriceMxn(ntd);

  // Count a calculator use once per page load, after the customer has
  // settled on a number (not on every keystroke).
  const tracked = useRef(false);
  useEffect(() => {
    if (!price || tracked.current) return undefined;
    const timer = setTimeout(() => {
      tracked.current = true;
      trackTtfStep(TTF_STEPS.calculatorUsed, { ntd: Number(ntd), priceMxn: price });
    }, 1500);
    return () => clearTimeout(timer);
  }, [ntd, price]);

  return (
    <div className="ttf-calculator">
      <h2 className="ttf-section-title ttf-calculator-title">{t('calculator.title')}</h2>
      <label className="ttf-calculator-pill">
        <span className="ttf-calculator-currency" aria-hidden="true">TWD</span>
        {/* Text + numeric keyboard instead of type="number": no spinner
            arrows, and only digits get through (festival prices are whole). */}
        <input
          id="ttf-calc-ntd"
          className="ttf-calculator-input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={7}
          placeholder={t('calculator.label')}
          aria-label={t('calculator.label')}
          value={ntd}
          onChange={(e) => setNtd(e.target.value.replace(/\D/g, ''))}
        />
        <output
          className={`ttf-calculator-result${price ? '' : ' is-empty'}`}
          htmlFor="ttf-calc-ntd"
          data-testid="ttf-calc-result"
          aria-live="polite"
        >
          {price ? formatTtfMxn(price, locale) : '$ —'}
          <span className="ttf-calculator-unit"> MXN</span>
        </output>
      </label>
    </div>
  );
};

export default TtfPriceCalculator;
